import { useState, useEffect, useRef } from "react";
import {
  Bot,
  Send,
  Loader2,
  Key,
  Settings2,
  Sparkles,
  Database,
  Table2,
  RefreshCw,
  ChevronDown,
  Copy,
  Check,
  Info,
  Lightbulb,
  BarChart3,
  Users,
  DollarSign,
  Package,
  HelpCircle,
  MessageSquarePlus,
  MessagesSquare,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import api from "@/services/api";

const UseCaseCard = ({ useCase, onSelect, selected }) => {
  const icons = {
    sales_prediction: BarChart3,
    customer_analysis: Users,
    financial_reporting: DollarSign,
    inventory_management: Package,
    custom: HelpCircle,
  };
  const Icon = icons[useCase.id] || Lightbulb;
  
  return (
    <button
      onClick={() => onSelect(useCase)}
      className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
        selected?.id === useCase.id
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${selected?.id === useCase.id ? "bg-primary text-white" : "bg-muted"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-medium">{useCase.name}</h4>
          <p className="text-xs text-muted-foreground mt-1">{useCase.description}</p>
        </div>
      </div>
    </button>
  );
};

const ChatMessage = ({ message, isUser }) => {
  const [copied, setCopied] = useState(false);
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Check if message contains code blocks
  const hasCodeBlock = message.includes("```");
  
  const formatMessage = (text) => {
    // Split by code blocks
    const parts = text.split(/(```[\s\S]*?```)/g);
    
    return parts.map((part, i) => {
      if (part.startsWith("```") && part.endsWith("```")) {
        // Code block
        const code = part.slice(3, -3).replace(/^\w+\n/, ""); // Remove language identifier
        return (
          <pre key={i} className="bg-slate-900 text-slate-100 p-3 rounded-lg my-2 overflow-x-auto text-sm">
            <code>{code}</code>
          </pre>
        );
      }
      // Regular text - convert newlines and basic markdown
      return (
        <span key={i} className="whitespace-pre-wrap">
          {part.split("\n").map((line, j) => {
            // Bold text
            line = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
            // Headers
            if (line.startsWith("### ")) {
              return <h4 key={j} className="font-semibold mt-3 mb-1">{line.slice(4)}</h4>;
            }
            if (line.startsWith("## ")) {
              return <h3 key={j} className="font-bold mt-4 mb-2 text-lg">{line.slice(3)}</h3>;
            }
            // List items
            if (line.startsWith("- ")) {
              return <li key={j} className="ml-4" dangerouslySetInnerHTML={{ __html: line.slice(2) }} />;
            }
            return <p key={j} dangerouslySetInnerHTML={{ __html: line }} />;
          })}
        </span>
      );
    });
  };
  
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[80%] ${isUser ? "order-first" : ""}`}>
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? "bg-primary text-primary-foreground rounded-br-md"
              : "bg-muted rounded-bl-md"
          }`}
        >
          <div className="text-sm">{formatMessage(message)}</div>
        </div>
        {!isUser && hasCodeBlock && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 h-7 text-xs"
            onClick={copyToClipboard}
          >
            {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
            {copied ? "Copied!" : "Copy response"}
          </Button>
        )}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xs font-bold">You</span>
        </div>
      )}
    </div>
  );
};

export default function AIAgent() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("ai_agent_api_key") || "");
  const [provider, setProvider] = useState(() => localStorage.getItem("ai_agent_provider") || "openai");
  const [model, setModel] = useState(() => localStorage.getItem("ai_agent_model") || "gpt-4o");
  const [showSettings, setShowSettings] = useState(!apiKey);
  
  const [useCases, setUseCases] = useState([]);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [availableTables, setAvailableTables] = useState({ bronze_tables: [], silver_tables: [] });
  const [loadingTables, setLoadingTables] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => `session_${Date.now()}`);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  useEffect(() => {
    fetchUseCases();
    fetchAvailableTables();
  }, []);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  useEffect(() => {
    if (apiKey) localStorage.setItem("ai_agent_api_key", apiKey);
    if (provider) localStorage.setItem("ai_agent_provider", provider);
    if (model) localStorage.setItem("ai_agent_model", model);
  }, [apiKey, provider, model]);

  const startNewConversation = async () => {
    // Clear server-side session
    try {
      await api.clearAIAgentSession(sessionId);
    } catch (error) {
      console.error("Failed to clear session:", error);
    }
    // Reset client-side state
    setMessages([]);
    setSelectedUseCase(null);
    setInputMessage("");
    setSessionId(`session_${Date.now()}`);
    toast.success("Started new conversation");
  };
  
  const fetchUseCases = async () => {
    try {
      const data = await api.getAIAgentUseCases();
      setUseCases(data.use_cases || []);
    } catch (error) {
      console.error("Failed to fetch use cases:", error);
    }
  };
  
  const fetchAvailableTables = async () => {
    setLoadingTables(true);
    try {
      const data = await api.getAIAgentAvailableTables();
      setAvailableTables(data);
    } catch (error) {
      console.error("Failed to fetch tables:", error);
    } finally {
      setLoadingTables(false);
    }
  };
  
  const handleUseCaseSelect = (useCase) => {
    setSelectedUseCase(useCase);
    if (useCase.suggested_prompt && useCase.id !== "custom") {
      setInputMessage(useCase.suggested_prompt);
    }
  };
  
  const sendMessage = async () => {
    if (!inputMessage.trim()) return;
    if (!apiKey) {
      toast.error("Please enter your API key first");
      setShowSettings(true);
      return;
    }
    
    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setSending(true);
    
    try {
      const response = await api.sendAIAgentMessage({
        api_key: apiKey,
        provider,
        model,
        session_id: sessionId,
        message: userMessage,
        context: {
          bronze_tables: availableTables.bronze_tables,
          silver_tables: availableTables.silver_tables,
        },
      });
      
      setMessages(prev => [...prev, { role: "assistant", content: response.response }]);
    } catch (error) {
      console.error("AI Agent error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to get AI response. Please check your API key.";
      toast.error(errorMessage);
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${errorMessage}` }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const modelOptions = {
    openai: [
      { value: "gpt-4o", label: "GPT-4o (Recommended)" },
      { value: "gpt-4o-mini", label: "GPT-4o Mini (Faster)" },
    ],
    anthropic: [
      { value: "claude-sonnet", label: "Claude Sonnet 4.5" },
    ],
    gemini: [
      { value: "gemini-pro", label: "Gemini 2.5 Pro" },
      { value: "gemini-flash", label: "Gemini 2.5 Flash" },
    ],
  };
  
  const totalTables = availableTables.bronze_tables.length + availableTables.silver_tables.length;
  
  return (
    <div className="h-[calc(100vh-120px)] flex gap-4" data-testid="ai-agent-page">
      {/* Left Sidebar - Settings & Context */}
      <div className="w-80 flex-shrink-0 space-y-4 overflow-y-auto">
        {/* API Key Settings */}
        <Collapsible open={showSettings} onOpenChange={setShowSettings}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-primary" />
                    <CardTitle className="text-sm">API Settings</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {apiKey && <Badge variant="outline" className="text-xs text-green-600">Configured</Badge>}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showSettings ? "rotate-180" : ""}`} />
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs">API Key</Label>
                  <Input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="text-xs"
                    data-testid="api-key-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Provider</Label>
                    <Select value={provider} onValueChange={(v) => { setProvider(v); setModel(modelOptions[v][0].value); }}>
                      <SelectTrigger className="text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                        <SelectItem value="gemini">Google Gemini</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Model</Label>
                    <Select value={model} onValueChange={setModel}>
                      <SelectTrigger className="text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {modelOptions[provider]?.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
        
        {/* Use Cases */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-yellow-500" />
              <CardTitle className="text-sm">Use Cases</CardTitle>
            </div>
            <CardDescription className="text-xs">Select a use case to get started</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid gap-2">
              {useCases.map((uc) => (
                <UseCaseCard
                  key={uc.id}
                  useCase={uc}
                  onSelect={handleUseCaseSelect}
                  selected={selectedUseCase}
                />
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Available Tables */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-500" />
                <CardTitle className="text-sm">Available Data</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={fetchAvailableTables}>
                <RefreshCw className={`w-3 h-3 ${loadingTables ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <CardDescription className="text-xs">
              {totalTables} tables found • Best results after Bronze/Silver setup
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {availableTables.bronze_tables.length > 0 && (
              <div>
                <p className="text-xs font-medium text-orange-600 mb-1">Bronze ({availableTables.bronze_tables.length})</p>
                <div className="flex flex-wrap gap-1">
                  {availableTables.bronze_tables.map((t) => (
                    <Badge key={t.name} variant="outline" className="text-xs">
                      {t.name.split(".").pop()}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {availableTables.silver_tables.length > 0 && (
              <div>
                <p className="text-xs font-medium text-blue-600 mb-1">Silver ({availableTables.silver_tables.length})</p>
                <div className="flex flex-wrap gap-1">
                  {availableTables.silver_tables.map((t) => (
                    <Badge key={t.name} variant="outline" className="text-xs">
                      {t.name.split(".").pop()}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {totalTables === 0 && !loadingTables && (
              <div className="text-center py-4">
                <Info className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">
                  No tables found. Run pipelines first to populate Bronze/Silver layers.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Main Chat Area */}
      <Card className="flex-1 flex flex-col">
        <CardHeader className="py-3 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">AI Gold Table Designer</CardTitle>
                <CardDescription className="text-xs">
                  Describe your use case, get suggestions, and ask follow-up questions
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  <MessagesSquare className="w-3 h-3 mr-1" />
                  {messages.length} messages
                </Badge>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={startNewConversation}
                disabled={messages.length === 0}
                className="text-xs"
                data-testid="new-conversation-btn"
              >
                <MessageSquarePlus className="w-4 h-4 mr-1" />
                New Chat
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Messages */}
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full mb-4">
                <Bot className="w-12 h-12 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Welcome to AI Gold Table Designer</h3>
              <p className="text-muted-foreground text-sm max-w-md mb-4">
                I&apos;ll help you design optimal Gold layer tables for your analytics use cases.
                Select a use case from the left or describe what you want to achieve.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 max-w-md mb-3">
                <p className="text-xs text-purple-800">
                  <strong>💬 Conversation Mode:</strong> After I suggest a Gold table blueprint, 
                  feel free to ask follow-up questions to refine or discuss the design!
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-w-md">
                <p className="text-xs text-amber-800">
                  <strong>💡 Tip:</strong> For best results, set up your Bronze and Silver tables first.
                  I can then analyze your actual data structure to make better suggestions.
                </p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <ChatMessage key={i} message={msg.content} isUser={msg.role === "user"} />
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </CardContent>
        
        {/* Input */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={messages.length === 0 
                ? "Describe your analytics use case (e.g., 'I need a sales prediction dashboard')..." 
                : "Ask a follow-up question or request changes to the suggestion..."
              }
              className="min-h-[44px] max-h-32 resize-none"
              disabled={sending}
              data-testid="ai-chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || sending}
              className="h-auto px-4"
              data-testid="ai-send-btn"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </Card>
    </div>
  );
}
